import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1580014317999-e9f1936787a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBjYXIlMjBuaWdodCUyMHJlZCUyMHRhaWwlMjBsaWdodHN8ZW58MHx8fHwxNzg1MTE2NDE3fDA&ixlib=rb-4.1.0&q=85";

const lineParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
};
const line = {
  hidden: { y: "110%" },
  visible: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.6, 0.9]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Cinematic car at night with red tail lights"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-black"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-24 md:px-12 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="overline mb-6 flex items-center gap-3 text-primary"
        >
          <span className="h-px w-10 bg-primary" />
          24/7 Roadside Assistance
        </motion.p>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="visible"
          className="font-display text-white leading-[0.9]"
        >
          <span className="block overflow-hidden">
            <motion.span variants={line} className="block text-5xl sm:text-7xl lg:text-8xl">
              Breakdowns <span className="text-primary">Don&apos;t Wait…</span>
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span variants={line} className="block text-5xl sm:text-7xl lg:text-8xl">
              Neither Do <span className="text-primary">We!</span>
            </motion.span>
          </span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between"
        >
          <p className="max-w-md text-lg leading-relaxed text-white/70">
            Fast, dependable roadside and automotive service. From dead batteries
            to empty tanks — Storm&apos;s Roadside gets you moving again.
          </p>
          <button
            data-testid="hero-cta-btn"
            onClick={() => scrollTo("schedule")}
            className="group flex shrink-0 items-center gap-3 bg-primary px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-[1.03]"
          >
            Schedule Service
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
